import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  useTheme
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { es } from 'date-fns/locale';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  FilterList as FilterListIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  TableChart as TableChartIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useNotification } from '../../contexts/NotificationContext';
import reporteService from '../../services/reporteService';

// Componentes de gráficos
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

const ReportesView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [reporte, setReporte] = useState(null);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewType, setViewType] = useState('chart');
  const [chartType, setChartType] = useState('bar');
  
  // Filtros
  const [filtros, setFiltros] = useState({
    fechaInicio: new Date(new Date().getFullYear(), 0, 1), // Primer día del año actual
    fechaFin: new Date(),
    especialistaId: '',
    especialidadId: '',
    estado: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [especialistas, setEspecialistas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);

  // Colores para gráficos
  const COLORS = [
    '#5D1390', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b',
    '#5a5c69', '#858796', '#6f42c1', '#20c9a6', '#f8f9fc'
  ];

  // Información del reporte
  const reportesInfo = {
    'dashboard': {
      title: 'Dashboard Ejecutivo',
      description: 'Vista general con indicadores clave y gráficos resumen',
      filtrable: false,
      endpoint: 'stats'
    },
    'pacientes-especialidad': {
      title: 'Pacientes por Especialidad',
      description: 'Distribución de pacientes según especialidad',
      filtrable: true,
      chartTypes: ['pie', 'bar'],
      defaultChartType: 'pie',
      endpoint: 'pacientes-por-especialidad'
    },
    'citas-mensuales': {
      title: 'Citas Mensuales',
      description: 'Evolución del número de citas por mes',
      filtrable: true,
      chartTypes: ['line', 'bar'],
      defaultChartType: 'line',
      endpoint: 'citas-por-mes'
    },
    'citas-especialista': {
      title: 'Citas por Especialista',
      description: 'Distribución de citas atendidas por cada especialista',
      filtrable: true,
      chartTypes: ['bar', 'pie'],
      defaultChartType: 'bar',
      endpoint: 'citas-por-especialista'
    }
  };

  // Cargar información del reporte
  useEffect(() => {
    // Verificar si el reporte existe
    if (!reportesInfo[id]) {
      setError('El reporte solicitado no existe');
      setLoading(false);
      return;
    }

    setReporte(reportesInfo[id]);
    if (reportesInfo[id].defaultChartType) {
      setChartType(reportesInfo[id].defaultChartType);
    }

    // Si el reporte requiere filtros, cargar datos de especialistas y especialidades
    if (reportesInfo[id].filtrable) {
      const fetchFilterData = async () => {
        try {
          // Cargar especialistas
          const especialistasRes = await reporteService.getEspecialistas();
          setEspecialistas(especialistasRes.data);
          
          // Cargar especialidades
          const especialidadesRes = await reporteService.getEspecialidades();
          setEspecialidades(especialidadesRes.data);
        } catch (error) {
          console.error('Error loading filter data:', error);
        }
      };
      
      fetchFilterData();
    }

    // Cargar datos del reporte
    fetchReportData();
  }, [id, refreshKey]);

  // Cargar datos del reporte
  const fetchReportData = async () => {
    if (!reportesInfo[id]) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {};
      
      // Añadir filtros si el reporte es filtrable
      if (reportesInfo[id].filtrable) {
        if (filtros.fechaInicio) {
          params.fecha_inicio = filtros.fechaInicio.toISOString().split('T')[0];
        }
        if (filtros.fechaFin) {
          params.fecha_fin = filtros.fechaFin.toISOString().split('T')[0];
        }
        if (filtros.especialistaId) {
          params.especialista_id = filtros.especialistaId;
        }
        if (filtros.especialidadId) {
          params.especialidad_id = filtros.especialidadId;
        }
        if (filtros.estado) {
          params.estado = filtros.estado;
        }
      }
      
      const response = await reporteService.getReportData(reportesInfo[id].endpoint, params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setError(
        error.response?.data?.message || 
        'Error al cargar los datos del reporte. Intente nuevamente.'
      );
      showNotification('Error al cargar los datos del reporte', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Actualizar filtros
  const handleFilterChange = (field, value) => {
    setFiltros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Resetear filtros
  const handleResetFilters = () => {
    setFiltros({
      fechaInicio: new Date(new Date().getFullYear(), 0, 1),
      fechaFin: new Date(),
      especialistaId: '',
      especialidadId: '',
      estado: ''
    });
    setRefreshKey(prev => prev + 1);
  };

  // Exportar reporte
  const handleExportClick = (format) => {
    showNotification(`Exportando reporte en formato ${format}...`, 'info');
    // Aquí iría la lógica de exportación
  };

  // Renderizar filtros
  const renderFilters = () => {
    if (!reporte?.filtrable) return null;
    
    return (
      <Paper 
        elevation={1} 
        sx={{ p: 2, mb: 3, display: showFilters ? 'block' : 'none' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Filtros
          </Typography>
          <Box>
            <Button 
              size="small" 
              onClick={handleResetFilters}
              sx={{ mr: 1 }}
            >
              Resetear
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleApplyFilters}
            >
              Aplicar
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          {/* Filtro de fecha inicio */}
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Inicio"
                value={filtros.fechaInicio}
                onChange={(newValue) => handleFilterChange('fechaInicio', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Filtro de fecha fin */}
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Fin"
                value={filtros.fechaFin}
                onChange={(newValue) => handleFilterChange('fechaFin', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Filtro de especialista */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="especialista-label">Especialista</InputLabel>
              <Select
                labelId="especialista-label"
                value={filtros.especialistaId}
                onChange={(e) => handleFilterChange('especialistaId', e.target.value)}
                label="Especialista"
              >
                <MenuItem value="">Todos</MenuItem>
                {especialistas.map(esp => (
                  <MenuItem key={esp.id} value={esp.id}>
                    {esp.usuario.nombre} {esp.usuario.apellidos}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Filtro de especialidad */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="especialidad-label">Especialidad</InputLabel>
              <Select
                labelId="especialidad-label"
                value={filtros.especialidadId}
                onChange={(e) => handleFilterChange('especialidadId', e.target.value)}
                label="Especialidad"
              >
                <MenuItem value="">Todas</MenuItem>
                {especialidades.map(esp => (
                  <MenuItem key={esp.id} value={esp.id}>
                    {esp.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Filtro de estado (para reportes de citas) */}
          {id.includes('citas') && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="estado-label">Estado</InputLabel>
                <Select
                  labelId="estado-label"
                  value={filtros.estado}
                  onChange={(e) => handleFilterChange('estado', e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Programada">Programada</MenuItem>
                  <MenuItem value="Confirmada">Confirmada</MenuItem>
                  <MenuItem value="Realizada">Realizada</MenuItem>
                  <MenuItem value="Cancelada">Cancelada</MenuItem>
                  <MenuItem value="No asistió">No asistió</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  // Renderizar gráfico según tipo
  const renderChart = () => {
    if (!data || data.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No hay datos disponibles para mostrar.
          </Typography>
        </Box>
      );
    }

    // Adaptar a cada tipo de reporte
    switch (id) {
      case 'dashboard':
        return renderDashboardStats();
      case 'pacientes-especialidad':
        return chartType === 'pie' ? 
          renderPieChart(data, 'name', 'value') : 
          renderBarChart(data, 'name', 'value', 'Pacientes');
      case 'citas-mensuales':
        return chartType === 'line' ? 
          renderLineChart(data, 'name', 'citas', 'Citas') : 
          renderBarChart(data, 'name', 'citas', 'Citas');
      case 'citas-especialista':
        return chartType === 'bar' ? 
          renderBarChart(data, 'nombre', 'citas', 'Citas') : 
          renderPieChart(data, 'nombre', 'citas');
      default:
        return (
          <Box p={3} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              Tipo de reporte no configurado.
            </Typography>
          </Box>
        );
    }
  };

  // Renderizar dashboard
  const renderDashboardStats = () => {
    if (!data) return null;
    
    return (
      <Grid container spacing={3}>
        {/* Tarjetas de estadísticas */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              borderLeft: '4px solid #5D1390',
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              TOTAL PACIENTES
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
              {data.totalPacientes || 0}
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              {data.pacientesNuevosMes || 0} nuevos este mes
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              borderLeft: '4px solid #1cc88a',
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              CITAS HOY
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
              {data.citasHoy || 0}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#1cc88a' }}>
              {data.citasConfirmadas || 0} confirmadas
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              borderLeft: '4px solid #36b9cc',
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              TOTAL CITAS
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
              {data.totalCitas || 0}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#36b9cc' }}>
              Histórico acumulado
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              borderLeft: '4px solid #f6c23e',
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              EVALUACIONES
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
              {data.totalEvaluaciones || 0}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#f6c23e' }}>
              {data.totalHistorias || 0} historias clínicas
            </Typography>
          </Paper>
        </Grid>
        
        {/* Gráficos */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Citas Mensuales
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={data.citasPorMes || []}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ChartTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="citas"
                    name="Citas"
                    stroke="#5D1390"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Pacientes por Especialidad
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.pacientesPorEspecialidad || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label
                  >
                    {(data.pacientesPorEspecialidad || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Renderizar gráfico de barras
  const renderBarChart = (data, nameKey, valueKey, valueName) => {
    return (
      <Box height={400}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={nameKey} 
              angle={-45} 
              textAnchor="end" 
              height={70} 
            />
            <YAxis />
            <ChartTooltip />
            <Legend />
            <Bar 
              dataKey={valueKey} 
              name={valueName || valueKey} 
              fill="#5D1390" 
              background={{ fill: '#eee' }} 
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // Renderizar gráfico circular
  const renderPieChart = (data, nameKey, valueKey) => {
    return (
      <Box height={400}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={120}
              fill="#8884d8"
              dataKey={valueKey}
              nameKey={nameKey}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <ChartTooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // Renderizar gráfico de líneas
  const renderLineChart = (data, xAxisKey, valueKey, valueName) => {
    return (
      <Box height={400}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xAxisKey} />
            <YAxis />
            <ChartTooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey={valueKey}
              name={valueName || valueKey}
              stroke="#5D1390"
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // Renderizar tabla
  const renderTable = () => {
    // Esta función renderizaría una tabla con los datos
    // Por simplicidad no se implementa en detalle aquí
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body1" color="textSecondary">
          Vista de tabla en desarrollo.
        </Typography>
      </Box>
    );
  };

  // Navegar hacia atrás
  const handleBackClick = () => {
    navigate('/reportes');
  };

  // Refrescar datos
  const handleRefreshClick = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Cambiar entre vista de gráfico o tabla
  const handleViewTypeChange = (event, newValue) => {
    setViewType(newValue);
  };

  // Cambiar tipo de gráfico
  const handleChartTypeChange = (newType) => {
    setChartType(newType);
  };

  // Si hay error, mostrar mensaje
  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <PageHeader
          title="Error"
          breadcrumbs={[
            { text: 'Dashboard', to: '/dashboard' },
            { text: 'Reportes', to: '/reportes' },
            { text: 'Error' }
          ]}
          actionButton={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
            >
              Volver
            </Button>
          }
        />
        <Card>
          <CardContent>
            <Box p={5} textAlign="center">
              <Typography variant="h6" color="error" gutterBottom>
                Error al cargar el reporte
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {error}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshClick}
                sx={{ mt: 3 }}
              >
                Intentar nuevamente
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title={reporte?.title || 'Cargando...'}
        subtitle={reporte?.description}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Reportes', to: '/reportes' },
          { text: reporte?.title || 'Reporte' }
        ]}
        actionButton={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            Volver
          </Button>
        }
      />

      <Box mb={3}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              {/* Cambiar entre gráfico y tabla */}
              <Tabs
                value={viewType}
                onChange={handleViewTypeChange}
                aria-label="view type"
                sx={{ mr: 2 }}
              >
                <Tab
                  icon={<BarChartIcon />}
                  label="Gráfico"
                  value="chart"
                  aria-label="chart"
                />
                <Tab
                  icon={<TableChartIcon />}
                  label="Tabla"
                  value="table"
                  aria-label="table"
                />
              </Tabs>

              {/* Tipos de gráficos si aplica */}
              {viewType === 'chart' && reporte?.chartTypes && (
                <Box sx={{ ml: 2, borderLeft: 1, borderColor: 'divider', pl: 2 }}>
                  {reporte.chartTypes.includes('bar') && (
                    <Tooltip title="Gráfico de barras">
                      <IconButton
                        color={chartType === 'bar' ? 'primary' : 'default'}
                        onClick={() => handleChartTypeChange('bar')}
                      >
                        <BarChartIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {reporte.chartTypes.includes('line') && (
                    <Tooltip title="Gráfico de líneas">
                      <IconButton
                        color={chartType === 'line' ? 'primary' : 'default'}
                        onClick={() => handleChartTypeChange('line')}
                      >
                        <TimelineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {reporte.chartTypes.includes('pie') && (
                    <Tooltip title="Gráfico circular">
                      <IconButton
                        color={chartType === 'pie' ? 'primary' : 'default'}
                        onClick={() => handleChartTypeChange('pie')}
                      >
                        <PieChartIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* Mostrar/ocultar filtros */}
              {reporte?.filtrable && (
                <Box sx={{ ml: 2 }}>
                  <Chip
                    icon={<FilterListIcon />}
                    label={showFilters ? "Ocultar filtros" : "Mostrar filtros"}
                    onClick={() => setShowFilters(!showFilters)}
                    variant={showFilters ? "filled" : "outlined"}
                    color="primary"
                  />
                </Box>
              )}
            </Box>

            <Box>
              {/* Botones de acción */}
              <Tooltip title="Refrescar datos">
                <IconButton onClick={handleRefreshClick} sx={{ mr: 1 }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Exportar PDF">
                <IconButton onClick={() => handleExportClick('pdf')} sx={{ mr: 1 }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Imprimir">
                <IconButton onClick={() => window.print()} sx={{ mr: 1 }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartir">
                <IconButton onClick={() => showNotification('Función de compartir en desarrollo', 'info')}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Filtros */}
      {renderFilters()}

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {viewType === 'chart' ? renderChart() : renderTable()}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReportesView;